<template>
    <slot />
    <Teleport to="body">
        <pbl-dialog
            ref="dialogRef"
            :headline="t('reports.export.modal.headline')"
            click-shield
            class="export-dialog"
        >
            <div class="export-modal">
                <VeeFormSingleSelectField
                    name="componentTypes"
                    :label="t('reports.export.modal.selector.labels.componentType')"
                    :placeholder="t('formInputs.common.placeholders.selectOption')"
                    :options="componentTypeOptions"
                    :disabled="componentTypeOptions.length === 0"
                    class="export-modal__full-width"
                />
                <div
                    v-if="componentType"
                    :key="`component-${componentType}`"
                    class="export-modal__fields"
                >
                    <VeeFormSingleSelectField
                        name="exportType"
                        :label="t('reports.export.modal.selector.labels.reportType')"
                        :placeholder="t('formInputs.common.placeholders.selectOption')"
                        :options="reportTypeOptions"
                        :disabled="reportTypeOptions.length === 0"
                        class="export-modal__full-width"
                    />
                    <template v-if="exportType">
                        <div class="export-modal__row">
                            <VeeFormSingleSelectField
                                name="groupSid"
                                :label="t('reports.export.modal.selector.labels.group')"
                                :placeholder="t('formInputs.common.placeholders.selectOption')"
                                :options="groupOptions"
                                :disabled="groupOptions.length === 0"
                            />
                            <div>
                                <VeeFormMultiselectField
                                    v-if="isSummaryStatus"
                                    name="plantSid"
                                    input-id="plantSid"
                                    :label="t('reports.export.modal.selector.labels.plant')"
                                    :placeholder="t('formInputs.common.placeholders.selectOption')"
                                    :options="plantOptions"
                                    :disabled="plantOptions.length === 0 || !groupSid"
                                />
                                <VeeFormSingleSelectField
                                    v-else
                                    name="plantSid"
                                    input-id="plantSid"
                                    :label="t('reports.export.modal.selector.labels.plant')"
                                    :placeholder="t('formInputs.common.placeholders.selectOption')"
                                    :options="plantOptions"
                                    :disabled="plantOptions.length === 0 || !groupSid"
                                />
                            </div>
                            <VeeFormSingleSelectField
                                name="componentSids"
                                input-id="componentSids"
                                :label="t('reports.export.modal.selector.labels.componentIdTechnicalLocation')"
                                :placeholder="t('formInputs.common.placeholders.selectOption')"
                                :options="componentListOptions"
                                :disabled="componentListOptions.length === 0 || isSummaryStatus"
                            />
                        </div>
                        <div class="export-modal__row export-modal__row--dates">
                            <VeeFormInputField
                                name="startDate"
                                type="month"
                                :label="t('reports.export.modal.selector.labels.startDate')"
                                :disabled="isSummaryStatus"
                            />
                            <VeeFormInputField
                                name="endDate"
                                type="month"
                                :label="t('reports.export.modal.selector.labels.endDate')"
                                :disabled="isSummaryStatus"
                            />
                        </div>
                    </template>
                </div>
                <div
                    v-else
                    class="export-modal__empty-state"
                >
                    <pbl-icon name="check2-square" />
                    <span>
                        {{ t('reports.export.modal.emptyStateBody') }}
                    </span>
                </div>
            </div>
            <pbl-button
                slot="footer"
                variant="secondary"
                @click="closeModal"
            >
                {{ t('reports.export.modal.actions.cancel') }}
            </pbl-button>
            <pbl-button
                slot="footer"
                :disabled="!meta.valid || isExportPending"
                :loading="isExportPending"
                @click="onSubmit"
            >
                <pbl-icon slot="icon" name="filetype-xlsx" />
                {{ t('reports.export.modal.actions.export') }}
            </pbl-button>
        </pbl-dialog>
    </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, nextTick, ref, watch } from 'vue'
import { PebbleDialog } from '@pebble/core'
import { useField, useForm } from 'vee-validate'
import { provideReportsExportModal } from '@/pages/reports/export/useReportsExportModal'
import { exportValidationSchema } from '@/pages/reports/export/ReportsExportModal.form'
import { SelectOptions } from '@/components/form-inputs/common/select-option'
import { usePlantsHierarchyQuery } from '@/api/queries/plants/usePlantsHierarchyQuery'
import VeeFormSingleSelectField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeFormSingleSelectField.vue'
import VeeFormMultiselectField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeFormMultiselectField.vue'
import VeeFormInputField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeFormInputField.vue'
import { ComponentType, getAllowedComponentTypesForExportReports } from '@/configuration/component/component-types'
import {
    flattenTransformersHierarchy,
    transformerOptions,
    useTransformersHierarchyQuery,
} from '@/api/queries/transformers/useTransformersHierarchyQuery'
import {
    flattenGeneratorsHierarchy,
    generatorOptions,
    useGeneratorsHierarchyQuery,
} from '@/api/queries/generators/useGeneratorsHierarchyQuery'
import { TIMEFRAME_DEFAULT_END_DATE, endDateFormat, startDateFormat, TIMEFRAME_OLDEST_RECORD } from '@/utils/date-utils'
import {
    AllGroupsOptionExceptionForExportType,
    ReportExportType,
    ComponentTypeToReportTypes,
} from '@/configuration/report/report-export-types'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { useReportsExportMutation } from '@/api/queries/reports/useReportsExportQuery'
import { AxiosError, AxiosHeaders } from 'axios'
import { downloadFile, getFilename } from '@/utils/file-utils'
import { LanguageType } from '@/configuration/language/language-types'
import { usePermissions } from '@/composables/usePermissions'

const { t, locale } = useI18n()
const toasts = usePebbleToasts()
const { userPermissions } = usePermissions()

const isSettingFromComponent = ref(false)

const dialogRef = ref<PebbleDialog | null>(null)
const exportTypeRef = ref<number | undefined>(undefined)

const ALL_ITEMS_VALUE = -1

const { handleSubmit, resetForm, meta, setFieldValue } = useForm({
    validationSchema: computed(() => {
        return exportValidationSchema(exportTypeRef.value ?? ReportExportType.HistoricalAnalysis)
    }),
    initialValues: {
        componentTypes: undefined,
        exportType: undefined,
        groupSid: undefined,
        plantSid: undefined,
        componentSids: undefined,
        startDate: TIMEFRAME_OLDEST_RECORD,
        endDate: TIMEFRAME_DEFAULT_END_DATE,
    },
})

const { value: componentType } = useField<number | undefined>(() => 'componentTypes')
const { value: exportType } = useField<number | undefined>(() => 'exportType')
const { value: groupSid } = useField<number | undefined>(() => 'groupSid')
const { value: plantSid } = useField<number | number[] | undefined>(() => 'plantSid')
const { value: componentSids } = useField<string | undefined>(() => 'componentSids')

const { data: exportedReport, mutateAsync: mutateExportAsync, isPending: isExportPending } = useReportsExportMutation()

const { data } = usePlantsHierarchyQuery()

const componentTypeOptions = computed<SelectOptions<number>>(() => {
    const componentsWithExportPermission = getAllowedComponentTypesForExportReports(userPermissions.value)

    return componentsWithExportPermission?.map((e) => ({
        label: t(e.labelKey) ?? '-',
        value: e.value ?? 0,
    })) ?? []
})

const reportTypeOptions = computed<SelectOptions<number>>(() => {
    if (!componentType.value) {
        return []
    }

    const reportTypes = ComponentTypeToReportTypes[componentType.value as ComponentType] ?? []
    return reportTypes.map((e) => ({
        label: t(e.labelKey),
        value: e.value,
    }))
})

const groupOptions = computed<SelectOptions<number>>(() => {
    if (!exportType.value || !data.value?.plantGroups) {
        return []
    }

    const groupPlants =
        data.value?.plantGroups?.map((e) => ({
            label: e.name ?? '-',
            value: e.sid ?? 0,
        })) ?? []

    const allGroupsOption = {
        label: t('reports.export.modal.selector.options.allGroups'),
        value: ALL_ITEMS_VALUE,
    }

    return AllGroupsOptionExceptionForExportType.includeAllGroupsOption.includes(exportType.value)
        ? [allGroupsOption, ...groupPlants]
        : groupPlants
})

const plantOptions = computed<SelectOptions<number>>(() => {
    if (!groupSid.value) {
        return []
    }

    let selectedPlants = data.value?.plantGroups?.find((e) => groupSid.value === e.sid)?.plants ?? []

    if (groupSid.value === ALL_ITEMS_VALUE) {
        selectedPlants = data.value?.plantGroups?.flatMap((group) => group.plants ?? []) ?? []
    }

    return selectedPlants.map((plant) => ({
        label: plant.name ?? '-',
        value: plant.sid ?? 0,
    }))
})

const { data: transformersHierarchy } = useTransformersHierarchyQuery()
const { data: generatorsHierarchy } = useGeneratorsHierarchyQuery()

const transformerList = computed(() => {
    if (!transformersHierarchy.value?.plantGroups) {
        return []
    }

    return flattenTransformersHierarchy(transformersHierarchy.value.plantGroups)
})

const generatorList = computed(() => {
    if (!generatorsHierarchy.value?.plantGroups) {
        return []
    }

    return flattenGeneratorsHierarchy(generatorsHierarchy.value.plantGroups)
})

const componentListOptions = computed((): SelectOptions => {
    if (componentType.value === ComponentType.Transformer) {
        if (plantSid.value) {
            const filteredList = transformerList.value.filter((e) => e.plant.sid === plantSid.value)
            return transformerOptions(filteredList)
        }
        return transformerOptions(transformerList.value)
    }

    if (componentType.value === ComponentType.Generator) {
        if (plantSid.value) {
            const filteredList = generatorList.value.filter((e) => e.plant.sid === plantSid.value)
            return generatorOptions(filteredList)
        }
        return generatorOptions(generatorList.value)
    }

    return []
})

const isSummaryStatus = computed(() => {
    return (
        exportType.value === ReportExportType.TransformerOilAnalysisStatus ||
        exportType.value === ReportExportType.SummaryConditionStatus
    )
})

const checkIsSummaryStatus = (type: number | undefined) => {
    return (type === ReportExportType.TransformerOilAnalysisStatus || type === ReportExportType.SummaryConditionStatus)
}

const openExport = () => {
    exportTypeRef.value = undefined

    resetForm({
        values: {
            componentTypes: undefined,
            exportType: undefined,
            groupSid: undefined,
            plantSid: undefined,
            componentSids: undefined,
            startDate: TIMEFRAME_OLDEST_RECORD,
            endDate: TIMEFRAME_DEFAULT_END_DATE,
        },
    })

    dialogRef.value?.show()
}

const closeModal = () => {
    dialogRef.value?.hide()
}

const plantSidValue = (plantSid: number | number[]) => {
    if (isSummaryStatus.value && Array.isArray(plantSid)) {
        return plantSid.length > 0 ? plantSid : plantOptions.value.map((e) => e.value)
    }

    return Array.isArray(plantSid) ? plantSid : [plantSid]
}

const componentSidsValue = (componentSids: string | string[]) => {
    return Array.isArray(componentSids) ? componentSids : [componentSids]
}

const languageType = computed(
    () => (LanguageType[locale.value as unknown as LanguageType] as unknown as number) ?? LanguageType['en-GB'],
)

const onSubmit = handleSubmit(async (values) => {
    const exportData = {
        exportType: values.exportType!,
        plantSid: values.plantSid !== undefined ? plantSidValue(values.plantSid) : undefined,
        componentTypes: values.componentTypes ? [values.componentTypes] : undefined,
        componentSids: values.componentSids ? componentSidsValue(values.componentSids) : undefined,
        startDate: values.startDate ? startDateFormat(values.startDate) : undefined,
        endDate: values.endDate ? endDateFormat(values.endDate) : undefined,
        languageType: languageType.value,
    }

    try {
        await mutateExportAsync(exportData)

        if (exportedReport.value?.data && exportedReport.value.headers) {
            const filename = getFilename(exportedReport.value.headers as AxiosHeaders) ?? 'unknown'
            downloadFile(exportedReport.value.data, filename)
            closeModal()
            toasts?.success({
                message: t('reports.export.modal.toast.exportCompleted'),
                duration: 3000,
            })
        }
    } catch (err) {
        if (err instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
})

watch(componentType, (newComponentType) => {
    exportTypeRef.value = undefined

    resetForm({
        values: {
            componentTypes: newComponentType,
            exportType: undefined,
            groupSid: undefined,
            plantSid: undefined,
            componentSids: undefined,
            startDate: TIMEFRAME_OLDEST_RECORD,
            endDate: TIMEFRAME_DEFAULT_END_DATE,
        },
    })
})

watch(exportType, (newExportType) => {
    exportTypeRef.value = newExportType
    const newIsSummary = checkIsSummaryStatus(newExportType)

    resetForm({
        values: {
            componentTypes: componentType.value,
            exportType: newExportType,
            groupSid: undefined,
            plantSid: newIsSummary ? [] : undefined,
            componentSids: undefined,
            startDate: newIsSummary ? undefined : TIMEFRAME_OLDEST_RECORD,
            endDate: newIsSummary ? undefined : TIMEFRAME_DEFAULT_END_DATE,
        },
    })
})

watch(groupSid, () => {
    if (isSettingFromComponent.value) return

    setFieldValue('plantSid', isSummaryStatus.value ? [] : undefined)
    setFieldValue('componentSids', undefined)
})

const updateLocationFromComponent = async (groupSid: number | undefined, plantSid: number | undefined) => {
    if (!groupSid || !plantSid) {
        setFieldValue('plantSid', undefined)
        setFieldValue('groupSid', undefined)
        return
    }

    isSettingFromComponent.value = true
    setFieldValue('groupSid', groupSid)
    await nextTick()
    setFieldValue('plantSid', plantSid)
    isSettingFromComponent.value = false
}

watch(componentSids, async (newComponentSids) => {
    if (newComponentSids) {
        if (componentType.value === ComponentType.Transformer) {
            const selectedTransformer = transformerList.value.find((e) => e.transformer.id === newComponentSids)
            await updateLocationFromComponent(selectedTransformer?.plantGroup.sid, selectedTransformer?.plant.sid)
        }

        else if (componentType.value === ComponentType.Generator) {
            const selectedGenerator = generatorList.value.find((e) => e.generator.id === newComponentSids)
            await updateLocationFromComponent(selectedGenerator?.plantGroup.sid, selectedGenerator?.plant.sid)
        }
    }
})

provideReportsExportModal({
    openExport,
})
</script>

<style scoped lang="scss">
.export-dialog {
    --width: 70vw;
}

.export-modal {
    display: flex;
    flex-direction: column;
    gap: $spacing-m;

    &__full-width {
        width: 100%;
    }

    &__fields {
        display: flex;
        flex-direction: column;
        gap: $spacing-m;
    }

    &__row {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: $spacing-m;

        &--dates {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }

    &__empty-state {
        padding: 50px 0;

        display: flex;
        flex-direction: column;

        align-items: center;
        justify-content: center;
        gap: $spacing-s;

        color: $pbl-text-muted;

        pbl-icon {
            --size: 2rem;
        }
    }
}
</style>
